/**
 * ModelCostOverTimeChart - Stacked bar chart showing cost/tokens over time by model
 *
 * Features:
 * - Stacked bar chart with top 10 models + "Other" bucket
 * - Supports 5 metrics: cost, total_tokens, prompt_tokens, completion_tokens, cached_tokens
 * - Model-specific colors
 * - Dynamic Y-axis formatting based on metric
 * - Click-to-drill navigation with visual feedback
 * - Built with Recharts for reliable click handling
 */

import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { palette } from '../../utils/designTokens';
import { getChartColors } from '../../utils/chartColors';
import { serializeFilters } from '../../utils/urlBuilder';
import { buildCostDrilldownWindow, parseCostBucketTime } from '../../utils/costDrilldown';

/**
 * Format timestamp for X-axis display
 */
const formatTimestamp = (timestamp, timeWindow) => {
  if (!timestamp) return '';

  try {
    const date = new Date(timestamp);
    if (isNaN(date.getTime())) return '';

    // For 24h view, show time (HH:MM)
    if (timeWindow === '24h') {
      return date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      });
    }

    // For 7d/30d/90d, show date (MM/DD)
    return date.toLocaleDateString('en-US', {
      month: 'numeric',
      day: 'numeric',
    });
  } catch {
    return '';
  }
};

/**
 * Format Y-axis ticks based on metric type
 */
const formatYAxis = (value, metric) => {
  if (metric === 'cost_usd') {
    // Format as dollars
    if (value >= 1000) return `$${(value / 1000).toFixed(1)}K`;
    if (value >= 1) return `$${value.toFixed(0)}`;
    if (value >= 0.01) return `$${value.toFixed(2)}`;
    if (value > 0) return `$${value.toFixed(4)}`;
    return '$0';
  } else {
    // Format as tokens (K or M)
    if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `${(value / 1000).toFixed(0)}K`;
    return value.toFixed(0);
  }
};

/**
 * Format tooltip value based on metric type
 */
const formatTooltipValue = (value, metric) => {
  if (metric === 'cost_usd') {
    return `$${value.toFixed(4)}`;
  } else {
    return value.toLocaleString();
  }
};

/**
 * Process raw API data into Victory chart format
 * Victory expects: { modelName: [{x: timestamp, y: value}, ...] }
 */
const processChartData = (costOverTimeData, selectedMetric) => {
  if (!costOverTimeData || costOverTimeData.length === 0) {
    return { chartData: {}, modelList: [] };
  }

  // Step 1: Calculate total per model across all timestamps
  const modelTotals = {};

  costOverTimeData.forEach(({ models }) => {
    models.forEach((model) => {
      const modelName = model.model_name;
      let value = 0;

      if (selectedMetric === 'cost_usd') {
        value = model.cost_usd || 0;
      } else if (selectedMetric === 'total_tokens') {
        value = model.total_tokens || 0;
      } else if (selectedMetric === 'prompt_tokens') {
        value = model.prompt_tokens || 0;
      } else if (selectedMetric === 'completion_tokens') {
        value = model.completion_tokens || 0;
      } else if (selectedMetric === 'cached_tokens') {
        value = model.cached_tokens || 0;
      }

      modelTotals[modelName] = (modelTotals[modelName] || 0) + value;
    });
  });

  // Step 2: Get top 10 models sorted by total (descending)
  const sortedModels = Object.entries(modelTotals)
    .sort(([, a], [, b]) => b - a)
    .map(([name]) => name);

  const top10Models = sortedModels.slice(0, 10);
  const otherModels = sortedModels.slice(10);

  // Step 3: Build Victory data format - separate array per model
  const chartData = {};

  top10Models.forEach((modelName) => {
    chartData[modelName] = [];
  });

  if (otherModels.length > 0) {
    chartData['Other'] = [];
  }

  // Step 4: Populate data arrays
  costOverTimeData.forEach(({ timestamp, models }) => {
    // Add top 10 models
    top10Models.forEach((modelName) => {
      const model = models.find((m) => m.model_name === modelName);
      let value = 0;

      if (model) {
        if (selectedMetric === 'cost_usd') {
          value = model.cost_usd || 0;
        } else if (selectedMetric === 'total_tokens') {
          value = model.total_tokens || 0;
        } else if (selectedMetric === 'prompt_tokens') {
          value = model.prompt_tokens || 0;
        } else if (selectedMetric === 'completion_tokens') {
          value = model.completion_tokens || 0;
        } else if (selectedMetric === 'cached_tokens') {
          value = model.cached_tokens || 0;
        }
      }

      chartData[modelName].push({ x: parseCostBucketTime(timestamp), y: value });
    });

    // Add "Other" bucket (sum of remaining models)
    if (otherModels.length > 0) {
      let otherValue = 0;
      otherModels.forEach((modelName) => {
        const model = models.find((m) => m.model_name === modelName);
        if (model) {
          if (selectedMetric === 'cost_usd') {
            otherValue += model.cost_usd || 0;
          } else if (selectedMetric === 'total_tokens') {
            otherValue += model.total_tokens || 0;
          } else if (selectedMetric === 'prompt_tokens') {
            otherValue += model.prompt_tokens || 0;
          } else if (selectedMetric === 'completion_tokens') {
            otherValue += model.completion_tokens || 0;
          } else if (selectedMetric === 'cached_tokens') {
            otherValue += model.cached_tokens || 0;
          }
        }
      });
      chartData['Other'].push({ x: parseCostBucketTime(timestamp), y: otherValue });
    }
  });

  // Step 5: Build model list (top 10 + Other, largest at bottom for stacking)
  const modelList = [...top10Models];
  if (otherModels.length > 0) {
    modelList.push('Other');
  }

  return { chartData, modelList };
};

/**
 * ModelCostOverTimeChart Component
 */
const ModelCostOverTimeChart = ({ data, selectedMetric, timeWindow, appId, environment }) => {
  const navigate = useNavigate();
  const [highlightedModel, setHighlightedModel] = useState(null);

  // Process data
  const { chartData, modelList } = useMemo(
    () => processChartData(data, selectedMetric),
    [data, selectedMetric]
  );

  // Memoize model colors - compute once per modelList change
  const chartColors = useMemo(() => {
    return getChartColors(modelList);
  }, [modelList]);

  // Transform data for Recharts (needs flat array with all models as keys)
  // MUST be declared BEFORE any early returns to satisfy Rules of Hooks
  const rechartData = useMemo(() => {
    if (!chartData || modelList.length === 0) return [];

    // Get all unique timestamps
    const timestampSet = new Set();
    modelList.forEach(modelName => {
      chartData[modelName]?.forEach(point => {
        timestampSet.add(point.x.getTime());
      });
    });

    const timestamps = Array.from(timestampSet).sort((a, b) => a - b);

    return timestamps.map(ts => {
      const dataPoint = { timestamp: new Date(ts) };
      modelList.forEach(modelName => {
        const point = chartData[modelName]?.find(p => p.x.getTime() === ts);
        dataPoint[modelName] = point ? point.y : 0;
      });
      return dataPoint;
    });
  }, [chartData, modelList]);

  // Check if we have data (must compute after all hooks)
  const hasData = modelList.length > 0 && Object.keys(chartData).length > 0;

  const containerRef = React.useRef(null);

  if (!hasData) {
    return (
      <div
        style={{
          height: 300,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          color: palette.text.muted,
          fontSize: '13px',
        }}
      >
        <p>No cost data available</p>
        <p style={{ fontSize: '11px', marginTop: '4px' }}>Try adjusting your time range</p>
      </div>
    );
  }

  // Handle bar click for drilldown
  const handleBarClick = (modelName, data) => {
    if (!modelName || !data || !data.timestamp) {
      return;
    }

    // Don't navigate on "Other" clicks
    if (modelName === 'Other') {
      return;
    }

    // Highlight the clicked model
    setHighlightedModel(modelName);

    const { start_time: startTimeStr, end_time: endTimeStr } =
      buildCostDrilldownWindow(data.timestamp, timeWindow);

    // Map selectedMetric to sort parameter
    const sortMapping = {
      'cost': 'cost_desc',
      'total_tokens': 'total_tokens_desc',
      'prompt_tokens': 'prompt_tokens_desc',
      'completion_tokens': 'completion_tokens_desc',
      'cached_tokens': 'cached_tokens_desc'
    };
    const sortParam = sortMapping[selectedMetric] || 'cost_desc';

    // Navigate with filters after a brief highlight
    setTimeout(() => {
      setHighlightedModel(null);
      const filters = {
        model: modelName,
        start_time: startTimeStr,
        end_time: endTimeStr,
        sort: sortParam,
        env: environment,
        range: timeWindow,
        from: 'cost'  // Track that user came from Cost tab for breadcrumb
      };
      const url = `/app/${appId}/traces?${serializeFilters(filters)}`;
      navigate(url);
    }, 200);
  };

  return (
    <ResponsiveContainer width="100%" height={280} ref={containerRef}>
        <BarChart data={rechartData} margin={{ top: 10, right: 20, left: 20, bottom: 10 }} barCategoryGap="10%">
          <CartesianGrid strokeDasharray="3 3" stroke={palette.support.gridline} opacity={0.5} />
          <XAxis
            dataKey="timestamp"
            tickFormatter={(date) => formatTimestamp(date, timeWindow)}
            tick={{ fontSize: 10, fill: palette.text.muted }}
            stroke={palette.neutrals.borderSubtle}
          />
          <YAxis
            tickFormatter={(value) => formatYAxis(value, selectedMetric)}
            tick={{ fontSize: 10, fill: palette.text.muted }}
            stroke={palette.neutrals.borderSubtle}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: palette.neutrals.panelQuaternary,
              border: `1px solid ${palette.neutrals.borderStrong}`,
              borderRadius: '4px',
              fontSize: '12px',
              padding: '8px'
            }}
            labelStyle={{ color: palette.text.heading, fontWeight: 600 }}
            itemStyle={{ color: palette.text.primary }}
            labelFormatter={(date) => formatTimestamp(date, timeWindow)}
            formatter={(value, name) => {
              // Format the value with proper units
              let displayValue;
              if (selectedMetric === 'cost_usd') {
                displayValue = `$${value.toFixed(4)}`;
              } else {
                displayValue = value.toLocaleString();
              }

              return [displayValue, name];
            }}
            cursor={false}
          />
          <Legend
            wrapperStyle={{ fontSize: '10px', paddingTop: '10px' }}
            iconType="square"
          />

          {/* Stacked bars for each model */}
          {modelList.map((modelName) => (
            <Bar
              key={modelName}
              dataKey={modelName}
              stackId="stack"
              fill={chartColors[modelName]}
              fillOpacity={highlightedModel === modelName ? 0.9 : 0.7}
              cursor="pointer"
              onClick={(data) => handleBarClick(modelName, data)}
              onMouseEnter={() => setHighlightedModel(modelName)}
              onMouseLeave={() => setHighlightedModel(null)}
              stroke="none"
              isAnimationActive={false}
              radius={[4, 4, 0, 0]}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
  );
};

export default ModelCostOverTimeChart;
